import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';

const labels = { grant: 'Employee invited / permissions granted', reinvite: 'Employee reinvited', update: 'Permissions changed',
  suspend: 'Employee suspended', remove: 'Employee removed', restore: 'Employee reactivated',
  identity_verified_access_provisioned: 'Identity verified / access provisioned', employee_notification: 'Setup email delivery' };
const permissionLabel = key => ({ quotationsCreate: 'Create quotations', quotationsEdit: 'Edit quotations',
  quotationsView: 'View quotations', customerPdf: 'Customer PDF', workshopPdf: 'Workshop PDF', savedQuotes: 'Saved quotes',
  crm: 'CRM', settings: 'Settings', dashboard: 'Dashboard', optimizer: 'Roll Optimizer', stitchingRatesTab: 'Stitching & Tracks' }[key] || key);
export function employeePermissionChanges(event) {
  const before = event.before?.permissions || {}, after = event.after?.permissions || {};
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter(key => (before[key] === true) !== (after[key] === true))
    .map(key => `${permissionLabel(key)}: ${after[key] === true ? 'allowed' : 'removed'}`);
}

export default function EmployeeActivityHistory({ functions, accountId, workspaceUid, allowed }) {
  const [open, setOpen] = useState(false), [events, setEvents] = useState([]), [loading, setLoading] = useState(false);
  const [error, setError] = useState(''), [cursor, setCursor] = useState(''), [hasMore, setHasMore] = useState(false);
  const generation = useRef(0), busy = useRef(false);
  useEffect(() => {
    generation.current += 1; busy.current = false; setOpen(false); setEvents([]); setError(''); setLoading(false);
    setCursor(''); setHasMore(false);
    return () => { generation.current += 1; };
  }, [accountId, workspaceUid, allowed]);
  const load = async more => {
    if (!allowed || busy.current) return;
    const current = generation.current; busy.current = true; setLoading(true); setError('');
    try {
      const result = (await httpsCallable(functions, 'listEmployeeActivityHistory')({ accountId, workspaceUid, cursor: more ? cursor : '' })).data;
      if (current !== generation.current) return;
      setEvents(previous => more ? [...new Map([...previous, ...result.events].map(item => [item.id, item])).values()] : result.events);
      setCursor(result.cursor); setHasMore(result.hasMore === true);
    } catch (failure) {
      if (current === generation.current) setError(String(failure.code || '').includes('permission-denied')
        ? 'Only authorized owners and administrators can view employee activity.' : 'Employee activity could not be loaded. Please try again.');
    } finally {
      if (current === generation.current) { busy.current = false; setLoading(false); }
    }
  };
  if (!allowed || !accountId || !workspaceUid) return null;
  return <section aria-label="Employee Activity History" style={{ border: '1px solid #cbd5e1', borderRadius: 12, padding: 16 }}>
    <button type="button" onClick={() => { setOpen(value => !value); if (!open && !events.length) load(false); }} aria-expanded={open}>Employee Activity History</button>
    {open && <div>
      <p>Read-only history of employee access granted by your business.</p>
      {error && <p role="alert">{error}</p>}
      {loading && <p role="status">Loading employee activity...</p>}
      {!loading && !error && !events.length && <p>No employee activity recorded yet.</p>}
      {events.map(event => <article key={event.id} style={{ borderTop: '1px solid #cbd5e1', padding: '12px 0' }}>
        <strong>{labels[event.action] || 'Employee access updated'}</strong>
        <p>Employee: {event.employeeName || event.employeeEmail || event.affectedUid || 'Not recorded'}<br />
          By: {event.actorName || event.actorUid || 'Not recorded'}<br />
          {event.performedAt && Number.isFinite(Date.parse(event.performedAt)) ? new Date(event.performedAt).toLocaleString() : 'Date unavailable'}</p>
        {event.before?.role !== event.after?.role && event.after?.role && <p>Role: {event.before?.role || 'Not assigned'} to {event.after.role}</p>}
        <ul>{employeePermissionChanges(event).map(change => <li key={change}>{change}</li>)}</ul>
        {event.action === 'employee_notification' && <p>{event.smtpSent ? 'Setup email sent' : 'Setup email could not be sent; access was not removed.'}</p>}
      </article>)}
      {error && <button type="button" disabled={loading} onClick={() => load(false)}>Retry employee activity</button>}
      {hasMore && <button type="button" disabled={loading} onClick={() => load(true)}>Load More Employee Activity</button>}
    </div>}
  </section>;
}
